import { ReactElement, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, List, Tag, Typography, Space, Flex, Spin, Progress } from 'antd'
import { PiGridFour, PiPencilSimple, PiX } from 'react-icons/pi'
import { useIntl } from 'react-intl'
import { IOnboardingDepartment, QUICK_ADD_DEPARTMENTS } from './types'
import IntlMessages from '../../util/IntlMessages'
import { FrontendUrls } from '../../types/urls'

const { Text } = Typography

interface DepartmentsStepProps {
  departments: IOnboardingDepartment[]
  departmentLimit: number
  loading?: boolean
  onUpdate: (departments: IOnboardingDepartment[]) => void
  onConfirm: () => void
  onSkip: () => void
  onNext?: () => void
  readOnly?: boolean
  isSubmitting?: boolean
  submitProgress?: number
  planLimitExceeded?: boolean
}

const PROGRESS_LABELS: Array<{ threshold: number; key: string }> = [
  { threshold: 15, key: 'onboarding.setup.import.progress.locations' },
  { threshold: 30, key: 'onboarding.setup.import.progress.leaveTypes' },
  { threshold: 45, key: 'onboarding.setup.import.progress.newLeaveTypes' },
  { threshold: 60, key: 'onboarding.setup.import.progress.policies' },
  { threshold: 75, key: 'onboarding.setup.import.progress.departments' },
  { threshold: 90, key: 'onboarding.setup.import.progress.holidays' },
  { threshold: 100, key: 'onboarding.setup.import.progress.done' },
]

const DepartmentsStep = ({
  departments,
  departmentLimit,
  loading = false,
  onUpdate,
  onConfirm,
  onSkip,
  onNext,
  readOnly,
  isSubmitting,
  submitProgress = 0,
  planLimitExceeded,
}: DepartmentsStepProps): ReactElement => {
  const { formatMessage } = useIntl()
  const [inputValue, setInputValue] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')

  const existingNames = new Set(departments.map((d) => d.name.toLowerCase()))
  const atLimit = departments.length >= departmentLimit
  const overLimit = departments.length > departmentLimit

  const handleAdd = (name: string) => {
    const trimmed = name.trim()
    if (!trimmed || existingNames.has(trimmed.toLowerCase()) || atLimit) return
    onUpdate([...departments, { id: crypto.randomUUID(), name: trimmed }])
    setInputValue('')
  }

  const handleRemove = (id: string) => {
    onUpdate(departments.filter((d) => d.id !== id))
  }

  const handleStartEdit = (dept: IOnboardingDepartment) => {
    setEditingId(dept.id)
    setEditDraft(dept.name)
  }

  const handleSaveEdit = () => {
    const trimmed = editDraft.trim()
    if (trimmed && editingId) {
      onUpdate(departments.map((d) => (d.id === editingId ? { ...d, name: trimmed } : d)))
    }
    setEditingId(null)
    setEditDraft('')
  }

  const progressLabel = PROGRESS_LABELS.find((p) => submitProgress <= p.threshold)?.key ?? PROGRESS_LABELS[PROGRESS_LABELS.length - 1].key

  if (loading) {
    return (
      <Flex justify="center" style={{ padding: '60px 0' }}>
        <Spin />
      </Flex>
    )
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {readOnly && (
        <div className="vt-readonly-notice">
          <IntlMessages
            id="onboarding.setup.readOnly.message"
            values={{
              link: (
                <Link to={FrontendUrls.departments}>
                  <IntlMessages id="app.departments" />
                </Link>
              ),
            }}
          />
        </div>
      )}

      <div>
        <Typography.Title level={4} className="vt-onboarding-title">
          <IntlMessages id="onboarding.setup.departments.title" />
        </Typography.Title>
        <Text className="vt-onboarding-subtitle">
          <IntlMessages id="onboarding.setup.departments.subtitle" />
        </Text>
      </div>

      {!readOnly &&
        (atLimit ? (
          <Text type="secondary">
            <IntlMessages id="onboarding.setup.departments.limitReached" values={{ limit: departmentLimit }} />
          </Text>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              <div className="vt-field-label" style={{ marginBottom: 8 }}>
                <IntlMessages id="onboarding.setup.departments.quickAdd" />
              </div>
              <Flex wrap gap={8}>
                {QUICK_ADD_DEPARTMENTS.filter((name) => !existingNames.has(name.toLowerCase())).map((name) => (
                  <Button key={name} size="small" className="vt-quick-add-chip" onClick={() => handleAdd(name)}>
                    + {name}
                  </Button>
                ))}
              </Flex>
            </div>

            <Flex gap={8}>
              <Input
                placeholder={formatMessage({ id: 'onboarding.setup.departments.inputPlaceholder' })}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAdd(inputValue)}
                size="large"
                style={{ flex: 1 }}
              />
              <Button size="large" onClick={() => handleAdd(inputValue)} disabled={!inputValue.trim()}>
                <IntlMessages id="onboarding.setup.button.add" />
              </Button>
            </Flex>
          </Space>
        ))}

      {departments.length > 0 && (
        <List className="vt-location-list">
          {departments.map((dept) => (
            <List.Item
              key={dept.id}
              className="vt-location-list-item"
              actions={
                editingId === dept.id || readOnly
                  ? []
                  : [
                      <Button key="edit" type="text" size="small" className="vt-location-remove-btn" icon={<PiPencilSimple size={14} />} onClick={() => handleStartEdit(dept)} />,
                      ...(!dept.isDefault
                        ? [<Button key="remove" type="text" size="small" className="vt-location-remove-btn" icon={<PiX size={14} />} onClick={() => handleRemove(dept.id)} />]
                        : []),
                    ]
              }
            >
              {editingId === dept.id ? (
                <Flex gap={8} align="center" style={{ width: '100%' }}>
                  <Input
                    value={editDraft}
                    onChange={(e) => setEditDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveEdit()
                      if (e.key === 'Escape') {
                        setEditingId(null)
                        setEditDraft('')
                      }
                    }}
                    autoFocus
                    style={{ flex: 1 }}
                  />
                  <Button type="primary" size="small" onClick={handleSaveEdit} disabled={!editDraft.trim()}>
                    <IntlMessages id="app.save" />
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingId(null)
                      setEditDraft('')
                    }}
                  >
                    <IntlMessages id="app.cancel" />
                  </Button>
                </Flex>
              ) : (
                <Space>
                  <PiGridFour size={14} className="vt-location-pin-icon" />
                  <Text>{dept.name}</Text>
                  {dept.isDefault && (
                    <Tag className="vt-from-signup-tag">
                      <IntlMessages id="onboarding.setup.departments.defaultTag" />
                    </Tag>
                  )}
                </Space>
              )}
            </List.Item>
          ))}
        </List>
      )}

      {readOnly && onNext && (
        <Button block size="large" onClick={onNext}>
          <IntlMessages id="onboarding.setup.button.next" />
        </Button>
      )}

      {!readOnly && (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          {isSubmitting ? (
            <Space direction="vertical" style={{ width: '100%' }} size={8}>
              <Progress percent={submitProgress} status={submitProgress === 100 ? 'success' : 'active'} />
              <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                <IntlMessages id={progressLabel} />
              </Text>
            </Space>
          ) : (
            <>
              <Button
                type="primary"
                block
                size="large"
                className="vt-onboarding-confirm-btn"
                onClick={onConfirm}
                disabled={departments.length === 0 || overLimit || planLimitExceeded}
              >
                <IntlMessages id="onboarding.setup.departments.saveAndContinue" />
              </Button>
              <div className="vt-onboarding-skip">
                <Typography.Link onClick={onSkip}>
                  <IntlMessages id="onboarding.setup.skipForNow" />
                </Typography.Link>
              </div>
            </>
          )}
        </Space>
      )}
    </Space>
  )
}

export default DepartmentsStep
